import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import apiClient from '@/services/api-client';
import { apiConfig } from '@/config/api';

// Password validation function (same as in ChangePasswordForm)
const validatePassword = (password: string): string | null => {
  if (password.length < 8 || password.length > 100) {
    return 'Password must be between 8 and 100 characters long';
  }
  
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter (A-Z)';
  }
  
  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter (a-z)';
  }
  
  if (!/[0-9]/.test(password)) {
    return 'Password must contain at least one digit (0-9)';
  }
  
  if (!/[!@#$%^&*()\-_=+\[\]{}|;:,.<>?/~]/.test(password)) {
    return 'Password must contain at least one special character (!@#$%^&*()-_=+[]{}|;:,.<>?/~)';
  }
  
  return null;
};

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const email = searchParams.get('email');
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [tokenError, setTokenError] = useState('');

  // Validate token and email on mount
  useEffect(() => {
    if (!email || !token) {
      setTokenError('Invalid reset link. Missing email or token.');
      setIsValidatingToken(false);
    } else {
      setIsValidatingToken(false);
    }
  }, [email, token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    // Validate new password requirements
    const passwordValidationError = validatePassword(newPassword);
    if (passwordValidationError) {
      setError(passwordValidationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!email || !token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    setIsLoading(true);

    try {
      // Send reset password request with email and token in query params, passwords in body
      await apiClient.post(
        `${apiConfig.endpoints.auth.resetPassword}?email=${encodeURIComponent(email!)}&token=${encodeURIComponent(token!)}`,
        {
          new_password: newPassword,
          confirm_password: confirmPassword,
        }
      );

      setSuccess(true);
      setError('');
      
      // Redirect to login after successful password reset
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Password reset successfully. Please login with your new password.',
          },
        });
      }, 2000);
    } catch (err: any) {
      console.error('[ResetPasswordPage] Reset password error:', err);
      
      const errorDetail = err?.response?.data?.detail || '';
      const errorMessage = err?.response?.data?.message || '';
      const status = err?.response?.status;
      
      // Check if error is about password requirements (validation error)
      if (status === 422 || status === 400) {
        const errorText = (errorDetail || errorMessage || '').toLowerCase();
        if (errorText.includes('password') && 
            (errorText.includes('requirement') || 
             errorText.includes('must contain') || 
             errorText.includes('uppercase') || 
             errorText.includes('lowercase') || 
             errorText.includes('digit') || 
             errorText.includes('special') ||
             errorText.includes('length') ||
             errorText.includes('character'))) {
          // It's a password validation error
          setError(errorDetail || errorMessage || 'Password does not meet requirements');
        } else {
          // Other validation error
          setError(errorDetail || errorMessage || 'Validation error. Please check your input.');
        }
      } else if (status === 400 || status === 404) {
        // Invalid token or expired link
        setError(errorDetail || errorMessage || 'Invalid or expired reset link. Please request a new password reset.');
      } else {
        // Other error
        setError(errorDetail || errorMessage || 'An error occurred while resetting password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidatingToken) {
    return (
      <div className="auth-container" data-test-id="reset-password-page-container">
        <Card className="card absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-md">
          <CardContent className="p-6 text-center">
            <p>Validating reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="auth-container" data-test-id="reset-password-page-container">
        <Card className="card absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-md">
          <CardHeader>
            <CardTitle>Invalid Reset Link</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-red-600 mb-4">{tokenError}</div>
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
              data-test-id="go-to-login-button"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-container" data-test-id="reset-password-page-container">
      <Card
        className="card absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-md"
        data-test-id="reset-password-card"
      >
        <CardHeader data-test-id="reset-password-card-header">
          <CardTitle data-test-id="reset-password-title">Reset Password</CardTitle>
        </CardHeader>
        <CardContent data-test-id="reset-password-card-content">
          <form
            onSubmit={handleSubmit}
            className="form-group space-y-4"
            data-test-id="reset-password-form"
          >
            <div className="text-sm text-gray-600 mb-4">
              Enter your new password for <strong>{email}</strong>
            </div>
            
            <div data-test-id="new-password-field-wrapper">
              <Label
                htmlFor="new-password"
                className="form-label"
                data-test-id="new-password-label"
              >
                New Password
              </Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                className="input-full"
                disabled={isLoading || success}
                minLength={8}
                data-test-id="new-password-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Must be 8-100 characters with uppercase, lowercase, digit, and special character
              </p>
            </div>
            
            <div data-test-id="confirm-password-field-wrapper">
              <Label
                htmlFor="confirm-password"
                className="form-label"
                data-test-id="confirm-password-label"
              >
                Confirm New Password
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                className="input-full"
                disabled={isLoading || success}
                minLength={8}
                data-test-id="confirm-password-input"
              />
            </div>
            
            {error && (
              <div className="error-text" data-test-id="reset-password-error">
                {error}
              </div>
            )}
            
            {success && (
              <div className="text-green-600 text-sm bg-green-50 p-3 rounded" data-test-id="reset-password-success">
                Password reset successfully! Redirecting to login...
              </div>
            )}
            
            <Button
              type="submit"
              className="btn-primary input-full"
              disabled={isLoading || success}
              data-test-id="reset-password-submit-button"
            >
              {isLoading ? 'Resetting...' : success ? 'Redirecting...' : 'Reset Password'}
            </Button>
            
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/login')}
              className="w-full text-sm text-gray-600 hover:text-gray-900"
              disabled={isLoading || success}
              data-test-id="back-to-login-button"
            >
              Back to Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

